"use client";

import { boutiques } from "@/lib/products-data";
import { images } from "@/lib/images";

const ACCENT_GRADIENTS: Record<string, string> = {
  "frais-marche": "from-[#7d9b4e] to-[#4a5d2d]",
  "epicerie-fine": "from-[#8a6d3b] to-[#5d4a3b]",
  "beaute-parfums": "from-[#b87c8a] to-[#7a4d5b]",
  "hightech-electro": "from-[#4a5d7d] to-[#2d3a4e]",
  "mode-maison": "from-[#8a6d6d] to-[#5d4848]",
  "cave-exception": "from-[#6e1f2a] to-[#3d1119]",
  "bazar-deco": "from-[#a8835b] to-[#6e5238]",
  "pharmacie": "from-[#6e8a7d] to-[#3d524a]",
  "boulangerie-royale": "from-[#b8853b] to-[#7a5a22]",
  "comptoir-cafes": "from-[#5d4a3b] to-[#3a2d22]",
  "epicerie-monde": "from-[#c87b4a] to-[#7a4a2d]",
  "espace-enfant": "from-[#a87c5d] to-[#6a4d38]",
};

export function BoutiquesGrid() {
  return (
    <section id="categories" className="relative bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6">
        {/* Header — texte exact original */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="ornament-line mb-6">
              <span className="text-gold text-[11px] tracking-[0.35em] uppercase">
                Nos Boutiques
              </span>
            </div>
            <h2 className="font-display text-ink text-4xl md:text-6xl leading-[1.05] max-w-2xl">
              Douze univers,
              <br />
              <span className="gold-text italic">un seul écrin.</span>
            </h2>
          </div>
          <p className="text-ink/65 font-serif text-lg leading-relaxed max-w-md">
            Chaque allée d&apos;Hyper OR I est pensée comme une galerie. De
            l&apos;épicerie fine au high-tech, du terroir français aux plus
            belles maisons européennes — tout est curated pour l&apos;excellence.
          </p>
        </div>

        {/* Grid of 12 boutiques */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {boutiques.map((b, idx) => {
            const gradient = ACCENT_GRADIENTS[b.id] ?? "from-ink to-ink";
            const imgKey = `boutique:${b.id}` as keyof typeof images;
            const imgSrc = images[imgKey] as string | undefined;
            return (
              <a
                key={b.id}
                href={`#${b.id}`}
                className="group relative overflow-hidden rounded-2xl bg-ink text-cream min-h-[420px] flex flex-col transition-all duration-500 hover:-translate-y-1 hover:luxury-shadow"
              >
                {/* Top: hero image */}
                <div className="relative h-44 md:h-48 overflow-hidden">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={b.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${gradient}`}
                    ></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent"></div>
                  {/* Icon emblem */}
                  <span className="absolute top-4 right-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-cream/95 backdrop-blur text-gold text-2xl leading-none shadow-lg">
                    {b.icon}
                  </span>
                  {/* Boutique number */}
                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-ink/60 backdrop-blur text-cream text-[10px] tracking-[0.25em] uppercase">
                    Boutique {String(idx + 1).padStart(2, "0")}
                  </div>
                </div>

                {/* Bottom content area with gradient */}
                <div
                  className={`relative flex-1 flex flex-col justify-between p-6 md:p-7 bg-gradient-to-br ${gradient} opacity-95`}
                >
                  <div>
                    <h3 className="font-display text-cream text-2xl md:text-[1.65rem] leading-tight">
                      {b.name}
                    </h3>
                    <p className="text-cream/75 text-sm font-serif italic mt-1">
                      {b.subtitle}
                    </p>
                    <p className="text-cream/60 text-[12px] leading-relaxed mt-3 line-clamp-3 font-serif">
                      {b.description}
                    </p>
                  </div>

                  <div className="mt-6 flex items-end justify-between">
                    <div>
                      <div className="text-cream/55 text-[10px] tracking-[0.25em] uppercase mb-1">
                        Sélection
                      </div>
                      <div className="font-display text-cream text-2xl">
                        50
                        <span className="text-cream/55 text-xs ml-1 font-sans">
                          produits
                        </span>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 text-gold text-[10px] tracking-[0.25em] uppercase border-b border-gold/50 pb-1 group-hover:gap-3 group-hover:border-gold transition-all">
                      Découvrir
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14"></path>
                        <path d="m12 5 7 7-7 7"></path>
                      </svg>
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
